import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Combination, Term } from '../../Data/types';
import { ParticleCanvas } from '../ParticleCanvas/ParticleCanvas';

export interface TermHighlightSuccessState {
  id: number;
  type: 'success';
  combination?: Combination;
  product: Term;
  isNew: boolean;
}

export interface TermHighlightFailedState {
  id: number;
  type: 'failed';
  isNew: boolean;
}

export type TermHighlightState = TermHighlightSuccessState | TermHighlightFailedState;

interface TermHighlightProps {
  state: TermHighlightState | null;
  onDismiss: () => void;
}

export const TermHighlight: React.FC<TermHighlightProps> = ({ state, onDismiss }) => {
  const iconRef = useRef<HTMLDivElement>(null);

  return (
    <AnimatePresence>
      {state && (
        <motion.div
          key={state.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          onClick={onDismiss}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {/* Success particles with burst, to show when term is new */}
          {state.type === 'success' && state.isNew && (
            <ParticleCanvas
              lifetime="forever"
              originRef={iconRef}
              withBurst
              particleSize={1.1}
              speed={1.3}
            />
          )}

          <motion.div
            style={{ position: 'relative', zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            {/* Icon + glow wrapper */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>

              {/* Ring-out burst — only for new discoveries */}
              {state.type === 'success' && state.isNew && [0.05].map((delay, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0.3, opacity: 0.9 }}
                  animate={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 0.75 + i * 0.1, ease: 'easeOut', delay }}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    x: '-50%',
                    y: '-50%',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.85)',
                    boxShadow: '0 0 14px rgba(165,180,252,0.55)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />
              ))}

              {/* Glow pulse ring — always shown */}
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.55, 0.35] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  x: '-50%',
                  y: '-50%',
                  width: '330px',
                  height: '330px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.3) 20%, rgba(255,255,255,0.0) 70%)',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />

              {/* Icon container (spring pop) */}
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 320, damping: 18, delay: 0.05 }}
              >
                {/* "Unlocked new!" badge — only for new discoveries */}
                {state.type === 'success' && state.isNew && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.15 }}
                    style={{
                      textAlign: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      color: 'rgb(251, 191, 36)',
                      textShadow: '0 0 12px rgba(251, 191, 36, 0.7)',
                      marginBottom: 17,
                      textTransform: 'uppercase',
                    }}
                  >
                    New in <em>{state.product.categoryName}</em>
                  </motion.div>
                )}

                {/* Continuous scale pulse */}
                <motion.div
                  ref={iconRef}
                  animate={{
                    scale: [1, 1.06, 1],
                    boxShadow: [
                      'rgba(255, 255, 255, 0.4) 0 0 50px 0px',
                      'rgba(255, 255, 255, 0.2) 0 0 20px 0px',
                      'rgba(255, 255, 255, 0.4) 0 0 50px 0px',
                    ],
                  }}
                  transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: 30,
                    padding: '10px 30px',
                    border: 'solid',
                    borderWidth: '6px',
                    borderColor: state.type === 'failed' ? '#f97316'
                        : state.isNew ? 'rgb(251, 191, 36)' : '#6366f1',
                    backgroundColor: state.isNew ? 'rgb(103, 89, 54)' : '#0f0f1a',
                    fontSize: 75,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    boxShadow: 'rgba(255, 255, 255, 0.3) 0 0 30px 0px',
                  }}
                >
                  {state.type === 'failed' ? '💩' : state.product.emoji}

                  {/* Term name — only for success */}
                  {state.type === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35, delay: 0.2 }}
                      style={{
                        fontSize: 28,
                        fontWeight: 600,
                        color: '#ffffff',
                        letterSpacing: '0.02em',
                        textShadow: '0 2px 16px rgba(0,0,0,0.6)',
                        marginBottom: 10,
                        textAlign: 'center',
                        padding: '12px 24px',
                      }}
                    >
                      {state.product.displayName}
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>
            </div>

{/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, delay: 0.32 }}
              style={{
                fontSize: 16,
                color: state.type === 'failed' ? '#fdba74'
                    : state.isNew ? 'rgb(189, 148, 44)' : '#a5b4fc',
                fontStyle: 'italic',
                textAlign: 'center',
                maxWidth: 300,
                lineHeight: 1.5,
                padding: '0 24px',
                textShadow: '0 1px 8px rgba(0,0,0,0.5)',
              }}
            >
              {state.type === 'failed'
                ? "This doesn't create anything interesting"
                : state.product.description}
            </motion.div>

            {/* Example — shown for success when available */}
            {state.type === 'success' && state.product.example && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, delay: 0.48 }}
                style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.45)',
                  textAlign: 'center',
                  maxWidth: 300,
                  lineHeight: 1.55,
                  padding: '8px 24px 0',
                  textShadow: '0 1px 6px rgba(0,0,0,0.4)',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                  marginTop: 10,
                }}
              >
                {state.product.example}
              </motion.div>
            )}

            {/* Tap hint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.2, 0.9, 0.2] }}
              transition={{ delay: 1.2, duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                marginTop: 20,
                fontSize: 13,
                color: 'rgba(255,255,255,1)',
                letterSpacing: '0.08em',
              }}
            >
              TAP TO CONTINUE
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
